// ===========================================
// Note Builder - AI-Powered Note Creation
// ===========================================
// Chat-based AI assistant for building knowledge notes
// 
// FLOW:
// 1. Select topics (AI, App Dev, Architecture domains)
// 2. Click "Ask AI" to start researching
// 3. Continue the conversation to dive deeper
// 4. Reformat responses as needed
// 5. Save as Note - opens edit modal for customization

import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { aiAPI, projectAPI } from '../services/api';
import { useProjects } from '../context/ProjectContext';
import {
  X,
  Bot,
  User,
  Send,
  Loader,
  BookOpen,
  Check,
  Copy,
  Trash2,
  ChevronRight,
  ChevronDown,  // For status dropdown
  Save,
  Sparkles,
  FolderOpen,   // For project buttons in Enrich
  // Topic icons - AI + App Dev + Architecture blend
  Brain,
  FileSearch,
  MessageSquare,
  Waypoints,
  Boxes,
  Database,
  Plug,
  Layers,
  KeyRound,
  Radio,
  GitBranch,
  Gauge,
  Activity,
  Shield,
  FlaskConical,
  // Reformat icons
  List,
  ListOrdered,
  FileText,
  Minimize2,
  SquareCode,
  ScrollText  // Build Note button - compile conversation into note
} from 'lucide-react';
import './NoteBuilder.scss';

// ===========================================
// Topics - AI + App Dev + Architecture Blend
// ===========================================
// Research domains for building AI-intensive micro SaaS applications
const TOPICS = [
  // AI Core - The intelligence layer
  { key: 'llms', label: 'LLMs', icon: Brain, color: '#8b5cf6' },
  { key: 'rag', label: 'RAG', icon: FileSearch, color: '#22c55e' },
  { key: 'agents', label: 'Agents', icon: Bot, color: '#f59e0b' },
  { key: 'prompts', label: 'Prompts', icon: MessageSquare, color: '#ec4899' },
  { key: 'vectors', label: 'Vectors', icon: Waypoints, color: '#06b6d4' },
  
  // Application Core - The product layer
  { key: 'architecture', label: 'Architecture', icon: Boxes, color: '#6366f1' },
  { key: 'data-modeling', label: 'Data Modeling', icon: Database, color: '#4db33d' },
  { key: 'apis', label: 'APIs', icon: Plug, color: '#3b82f6' },
  { key: 'state', label: 'State', icon: Layers, color: '#a855f7' },
  { key: 'auth', label: 'Auth', icon: KeyRound, color: '#f97316' },
  { key: 'streaming', label: 'Streaming', icon: Radio, color: '#14b8a6' },
  
  // Production Core - The delivery layer
  { key: 'devops', label: 'DevOps', icon: GitBranch, color: '#64748b' },
  { key: 'performance', label: 'Performance', icon: Gauge, color: '#eab308' },
  { key: 'observability', label: 'Observability', icon: Activity, color: '#10b981' },
  { key: 'security', label: 'Security', icon: Shield, color: '#ef4444' },
  { key: 'testing', label: 'Testing', icon: FlaskConical, color: '#f472b6' }
];

// ===========================================
// Reformat Actions - Transform AI responses
// ===========================================
const REFORMAT_ACTIONS = [
  { 
    key: 'build-note', 
    label: 'Build Note', 
    icon: ScrollText, 
    // Compile entire conversation into a well-structured note ready to save
    prompt: `Compile our entire conversation into one well-structured note. Format it as:

# [Topic Title]

## Overview
A brief 2-3 sentence summary of what was discussed.

## Key Points
- Main takeaways and insights
- Important concepts covered
- Practical applications

## Details
Expand on the most important information discussed.

## Quick Reference
Any code snippets, commands, or formulas worth remembering.

Make it scannable, informative, and ready to save as a reference note.` 
  },
  { 
    key: 'bullets', 
    label: 'Bullets', 
    icon: List, 
    prompt: 'Reformat this as clear bullet points for easy scanning.' 
  },
  { 
    key: 'steps', 
    label: 'Steps', 
    icon: ListOrdered, 
    prompt: 'Reformat this as numbered step-by-step instructions.' 
  },
  { 
    key: 'keypoints', 
    label: 'Key Points', 
    icon: FileText, 
    prompt: 'Extract and list only the key points and takeaways.' 
  },
  { 
    key: 'tldr', 
    label: 'TL;DR', 
    icon: Minimize2, 
    prompt: 'Give me a TL;DR - summarize in 2-3 sentences.' 
  },
  { 
    key: 'code', 
    label: 'Code Example', 
    icon: SquareCode, 
    prompt: 'Can you provide a code example that demonstrates this concept?' 
  },
  { 
    key: 'eli5', 
    label: 'ELI5', 
    icon: MessageSquare, 
    prompt: 'Explain like I\'m 5 - make it super simple and use analogies.' 
  }
];

// ===========================================
// Project Status Filter Options
// ===========================================
// Filter projects by status in the Enrich dropdown
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'idea', label: 'Idea' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Done' }
];

// ===========================================
// Build Project Context Prompt
// ===========================================
// Uses AI-generated readmeSummary + enrichedContext when available
// Falls back to basic metadata + README excerpt if not
const buildProjectContext = (project) => {
  let parts = [];
  
  // ===========================================
  // Priority 1: Use AI-Generated README Summary
  // ===========================================
  // Condensed overview with key topics - perfect for quick context
  if (project.readmeSummary) {
    parts.push(`**📋 README Summary:**\n${project.readmeSummary}`);
  }
  
  // ===========================================
  // Priority 2: Include AI-Generated Enriched Context
  // ===========================================
  // This provides architecture, patterns, key differentiators, and security model
  // Much more useful for note context than raw metadata
  if (project.enrichedContext) {
    // If we have summary, add a separator before enriched context
    if (parts.length > 0) {
      parts.push('\n---\n');
    }
    parts.push(project.enrichedContext);
    parts.push(`\n*Use this project context to tailor your response.*`);
    return parts.join('\n');
  }
  
  // ===========================================
  // Fallback: Build basic context from metadata
  // ===========================================
  // Used when enrichedContext hasn't been generated yet (no README uploaded)
  const tech = project.techStack?.map(t => t.name).join(', ') || 'Not specified';
  
  // If we don't have summary either, start fresh with project name
  if (parts.length === 0) {
    parts.push(`**📁 Project: ${project.name}**`);
  }
  
  if (project.description) {
    parts.push(`**Description:** ${project.description}`);
  }
  if (project.techStack?.length > 0) {
    parts.push(`**Tech Stack:** ${tech}`);
  }
  // Include README excerpt as fallback if no enrichedContext or summary
  if (project.readmeContent && !project.readmeSummary) {
    const excerpt = project.readmeContent.substring(0, 1200);
    const truncated = project.readmeContent.length > 1200 ? '...' : '';
    parts.push(`\n**README:**\n\`\`\`\n${excerpt}${truncated}\n\`\`\``);
  }
  
  parts.push(`\n*Use this project context to tailor your response.*`);
  return parts.join('\n');
};

// ===========================================
// Main Component
// ===========================================
const NoteBuilder = ({
  isOpen,
  onClose,
  onSaveAsNote  // Callback to save content as a note
}) => {
  // Project context for Enrich feature
  const { currentProject, projects, fetchProjects, loading: projectsLoading } = useProjects();
  
  // ===========================================
  // State
  // ===========================================
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Topic selection (multiple allowed)
  const [selectedTopics, setSelectedTopics] = useState([]);
  
  // Clipboard feedback
  const [copiedId, setCopiedId] = useState(null);
  
  // Save state
  const [saving, setSaving] = useState(false);
  
  // Enrich status filter
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Track if we've already fetched projects for this modal session
  // Prevents infinite loop from dependency array triggering repeated fetches
  const hasFetchedRef = useRef(false);

  // ===========================================
  // Auto-scroll to latest message
  // ===========================================
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===========================================
  // Initialize on open
  // ===========================================
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      
      // Show welcome message
      if (messages.length === 0) {
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: `📝 **Note Builder**

I'll help you learn and capture knowledge on any dev topic.

**How to use:**
1. (Optional) Pick a **topic** for context
2. Type your question or what you want to learn
3. Use **"I want to"** buttons to guide the conversation
4. **Reformat** responses for the perfect note
5. Click **Save as Note** to capture it

What would you like to learn about?`
        }]);
      }
      
      // Fetch projects for Enrich feature (only once per modal open)
      // Using ref to prevent infinite loop from projectsLoading/projects.length dependencies
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchProjects();
      }
    } else {
      // Reset the flag when modal closes so next open fetches fresh data
      hasFetchedRef.current = false;
    }
  }, [isOpen, messages.length, fetchProjects]);

  // ===========================================
  // Handlers
  // ===========================================
  
  // Clear chat and reset state
  const handleClear = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `📝 **Note Builder**

I'll help you learn and capture knowledge on any dev topic.

**How to use:**
1. (Optional) Pick a **topic** for context
2. Type your question or what you want to learn
3. Use **"I want to"** buttons to guide the conversation
4. **Reformat** responses for the perfect note
5. Click **Save as Note** to capture it

What would you like to learn about?`
    }]);
    setSelectedTopics([]);
    setInput('');
  };

  // Copy message to clipboard
  const handleCopy = async (message) => {
    try {
      // Extract code block if present
      const codeMatch = message.content.match(/```(?:\w+)?\n([\s\S]*?)```/);
      const text = codeMatch ? codeMatch[1].trim() : message.content;
      
      await navigator.clipboard.writeText(text);
      setCopiedId(message.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Toggle topic selection (multiple allowed)
  const handleTopicClick = (topicKey) => {
    setSelectedTopics(prev => 
      prev.includes(topicKey)
        ? prev.filter(k => k !== topicKey)  // Remove if already selected
        : [...prev, topicKey]               // Add if not selected
    );
  };

  // Send reformat action
  const handleReformatClick = (action) => {
    sendMessage(action.prompt, true);
  };

  // Inject project context (README.md enrichment)
  // Fetches full project data to ensure readmeContent is included
  const handleEnrichClick = async (project) => {
    try {
      // Fetch full project data (list excludes readmeContent for performance)
      const response = await projectAPI.get(project._id || project.id);
      const fullProject = response.data.data.project;
      
      // Build and send context with README
      sendMessage(buildProjectContext(fullProject));
    } catch (err) {
      console.error('Failed to fetch project for enrichment:', err);
      // Fall back to using cached project data (without README)
      sendMessage(buildProjectContext(project));
    }
  };

  // Generate initial response from selected topics
  const handleGenerate = () => {
    if (loading || selectedTopics.length === 0) return;
    
    // Get selected topic labels
    const topicLabels = selectedTopics
      .map(key => TOPICS.find(t => t.key === key)?.label)
      .filter(Boolean);
    
    // Build prompt based on selected topics
    let prompt = '';
    if (topicLabels.length === 1) {
      prompt = `I want to learn about **${topicLabels[0]}**. What are the key concepts, best practices, and important things I should know?`;
    } else {
      prompt = `I want to learn about **${topicLabels.join('**, **')}**. How do these topics relate, what are the key concepts for each, and what best practices should I know?`;
    }
    
    sendMessage(prompt);
  };

  // ===========================================
  // Send Message to AI
  // ===========================================
  const sendMessage = async (messageText, isReformat = false) => {
    if (!messageText?.trim() || loading) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim(),
      isReformat
    };

    setMessages(prev => [...prev, userMessage]);
    if (!isReformat) setInput('');
    setLoading(true);

    try {
      // Build conversation history (last 10 messages)
      const history = messages
        .filter(m => m.id !== 'welcome')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      // Call AI API
      const response = await aiAPI.chat({
        message: messageText,
        conversationHistory: history,
        projectId: currentProject?._id || currentProject?.id
      });

      if (!response?.data?.success || !response?.data?.data?.message) {
        throw new Error(response?.data?.error || 'No response from AI');
      }

      // Add AI response
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.data.message
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      // Handle error - could be string or object
      let errorMsg = "AI unavailable";
      if (err?.response?.data?.error) {
        // Error might be string or object with message property
        const apiError = err.response.data.error;
        errorMsg = typeof apiError === 'string' ? apiError : (apiError.message || JSON.stringify(apiError));
      } else if (err?.message) {
        errorMsg = err.message;
      }
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Something went wrong: ${errorMsg}. Try again.`,
        isError: true
      }]);
    }

    setLoading(false);
  };

  // ===========================================
  // Save as Note
  // ===========================================
  const handleSaveAsNote = async () => {
    // Find last AI message (not welcome)
    const lastAiMsg = [...messages]
      .reverse()
      .find(m => m.role === 'assistant' && m.id !== 'welcome' && !m.isError);
    
    if (!lastAiMsg) return;
    
    setSaving(true);
    
    // Pass content and selected topic to parent (use first selected or default to 'ai')
    await onSaveAsNote({
      content: lastAiMsg.content,
      topic: selectedTopics[0] || 'ai'
    });
    
    setSaving(false);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // ===========================================
  // Render
  // ===========================================
  if (!isOpen) return null;

  // Filter projects by status for Enrich dropdown
  const filteredProjects = projects.filter(p => 
    statusFilter === 'all' || p.status === statusFilter
  );

  return (
    <div className="note-builder-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="note-builder">
        {/* ===========================================
            Header
        =========================================== */}
        <div className="note-builder__header">
          <div className="note-builder__title">
            <BookOpen size={18} />
            <span>Note Builder</span>
          </div>
          <div className="note-builder__header-actions">
            <button 
              className="note-builder__clear-btn"
              onClick={handleClear}
              title="Clear chat"
              disabled={messages.length <= 1}
            >
              <Trash2 size={16} />
            </button>
            <button className="note-builder__close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ===========================================
            Topic Selector - Click topics then Ask AI
        =========================================== */}
        <div className="note-builder__topics">
          <div className="note-builder__topics-label">
            <ChevronRight size={12} />
            <span>Topics</span>
            <span className="note-builder__topics-hint">(select one or more)</span>
          </div>
          <div className="note-builder__topics-row">
            <div className="note-builder__topics-list">
              {TOPICS.map((topic) => {
                const Icon = topic.icon;
                const isSelected = selectedTopics.includes(topic.key);
                return (
                  <button
                    key={topic.key}
                    className={`note-builder__topic ${isSelected ? 'active' : ''}`}
                    onClick={() => handleTopicClick(topic.key)}
                    style={{ '--topic-color': topic.color }}
                    title={topic.label}
                  >
                    <Icon size={12} />
                    <span>{topic.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              className="note-builder__generate-btn"
              onClick={handleGenerate}
              disabled={loading || selectedTopics.length === 0}
              title={selectedTopics.length === 0 ? 'Select at least one topic' : 'Start learning about selected topics'}
            >
              {loading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
              <span>Ask AI</span>
            </button>
          </div>
        </div>

        {/* ===========================================
            Chat Area
        =========================================== */}
        <div className="note-builder__chat">
          <div className="note-builder__messages">
            {messages.map((msg) => (
              <div 
                key={msg.id}
                className={`note-builder__message note-builder__message--${msg.role} ${msg.isReformat ? 'note-builder__message--reformat' : ''} ${msg.isError ? 'note-builder__message--error' : ''}`}
              >
                <div className="note-builder__avatar">
                  {msg.role === 'assistant' ? <Bot size={14} /> : <User size={14} />}
                </div>
                <div className="note-builder__content">
                  {/* Basic markdown rendering - sanitized to prevent XSS */}
                  <div dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(
                      msg.content
                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                        .replace(/`([^`]+)`/g, '<code>$1</code>')
                        .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
                        .replace(/\n/g, '<br>'),
                      { ALLOWED_TAGS: ['strong', 'code', 'pre', 'br'] }
                    )
                  }} />
                  
                  {/* Copy button for AI messages */}
                  {msg.role === 'assistant' && msg.id !== 'welcome' && (
                    <button
                      className={`note-builder__copy-btn ${copiedId === msg.id ? 'copied' : ''}`}
                      onClick={() => handleCopy(msg)}
                      title="Copy to clipboard"
                    >
                      {copiedId === msg.id ? (
                        <><Check size={12} /><span>Copied!</span></>
                      ) : (
                        <><Copy size={12} /><span>Copy</span></>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="note-builder__message note-builder__message--assistant">
                <div className="note-builder__avatar"><Bot size={14} /></div>
                <div className="note-builder__typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Chat input */}
          <div className="note-builder__input-area">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a follow-up question..."
              disabled={loading}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="note-builder__send"
            >
              {loading ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>

        {/* ===========================================
            Enrich - README.md & Project Context
        =========================================== */}
        <div className="note-builder__enrich">
          <span className="note-builder__enrich-label">
            <Sparkles size={12} />
            Enrich:
          </span>
          
          {/* Status filter dropdown */}
          <div className="note-builder__enrich-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="note-builder__enrich-select"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={10} className="note-builder__enrich-select-icon" />
          </div>
          
          {/* Project buttons - click to inject context + README */}
          <div className="note-builder__enrich-projects">
            {projectsLoading ? (
              <span className="note-builder__enrich-loading">Loading...</span>
            ) : filteredProjects.length === 0 ? (
              <span className="note-builder__enrich-empty">No projects</span>
            ) : (
              filteredProjects.map((project) => (
                <button
                  key={project._id || project.id}
                  className="note-builder__enrich-project"
                  onClick={() => handleEnrichClick(project)}
                  disabled={loading}
                  title={`Inject ${project.name} context + README`}
                >
                  <FolderOpen size={11} />
                  <span>{project.name}</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ===========================================
            Reformat Actions
        =========================================== */}
        <div className="note-builder__reformat">
          <span className="note-builder__reformat-label">Reformat:</span>
          <div className="note-builder__reformat-actions">
            {REFORMAT_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  className="note-builder__reformat-btn"
                  onClick={() => handleReformatClick(action)}
                  disabled={loading || messages.length < 2}
                  title={action.prompt}
                >
                  <Icon size={12} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===========================================
            Footer - Save as Note / Cancel
        =========================================== */}
        <div className="note-builder__footer">
          <button className="note-builder__cancel" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="note-builder__save"
            onClick={handleSaveAsNote}
            disabled={
              saving || 
              loading ||  // Disable while AI is generating response
              !messages.some(m => m.role === 'assistant' && m.id !== 'welcome' && !m.isError)  // Must have valid AI message to save
            }
          >
            {saving ? (
              <><Loader size={14} className="animate-spin" />Saving...</>
            ) : (
              <><Save size={14} />Save as Note</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteBuilder;

